import { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { SearchUrlParams } from '@/types/search/SearchUrlParams';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { clearBrowseSeoMeta } from '@/redux/modules/browsePath/browsePath.reducer';
import { getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { FilterAction } from '@/types/SearchFacet';
import { removeBrowseFacetOptionFromPath } from '@/utils/browsePath/browsePath';
import { getCategoryBrowseNavigateUrl } from '@/utils/browsePath/buildCategoryBrowseNavigateUrl';
import { getCollectionConfig } from '@/utils/collections/collections.config';
import { CollectionLayout } from '@/utils/collections/collections.types';
import { getPathWithoutParams, modifyParamsRemovePage, parseQueryParams } from '@/utils/queryParams';
import { getArtSubjectPathSlugForSeoUrl, parseSearchSeoPath } from '@/utils/search/searchSeoPath';
import {
    getCollectionsUrlWithParams,
    getIsSEOSearchPage,
    getIsSEOSearchPageWithLocation,
    getSearchUrlWithParams,
    getSeoSearchUrlWithLocationParams,
    getSeoSearchUrlWithParams,
    isCategoryPage,
    isPriceGuidePage,
} from '@/utils/urls';

import { CurrentOpenMenu } from '../types';

type NavigateWithParamsProps = {
    browseFacetOptionRemovedId?: string | number;
    browseFacetOptionRemovedIds?: Array<string | number>;
    checkForPageRemoval?: boolean;
    clearSearchSeoSubjectFromPath?: boolean;
    currentOpenMenu?: CurrentOpenMenu;
    /**
     * The filter action that triggered this navigation, used for amplitude tracking
     */
    filterAction: FilterAction;
    params: SearchUrlParams;
    preventScrollReset?: boolean;
    /** When true, triggers "Search Completed" instead of "Search Refined" in analytics */
    suggestionClicked?: boolean;
};

export const useNavigateSearchPageWithParams = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const location = useLocation();
    const { pathname } = location;
    // Read through a ref: the query string changes on every filter and page navigation, and effects keyed on
    // the returned callback (e.g. useRedirectToValidPage) would re-run each time.
    const searchRef = useRef(location.search);
    searchRef.current = location.search;
    const rawFacets = useAppSelector(getSearchFacets);

    const isSEOSearchPage = getIsSEOSearchPage(pathname);
    const collection = getCollectionConfig(pathname);
    const isOnCategoryPage = isCategoryPage(pathname);
    const isOnPriceGuidePage = isPriceGuidePage(pathname);

    return useCallback(
        ({
            browseFacetOptionRemovedId,
            browseFacetOptionRemovedIds,
            checkForPageRemoval,
            clearSearchSeoSubjectFromPath,
            currentOpenMenu,
            filterAction,
            params,
            preventScrollReset,
            suggestionClicked,
        }: NavigateWithParamsProps) => {
            let newParams = params;
            if (checkForPageRemoval && params?.page === 1) {
                newParams = modifyParamsRemovePage(newParams);
            }

            /**
             * The legacy star-wars page carries its slug as an explicit `keyword` param, because
             * its slug *is* its search keyword. Config-driven collections must not do that: their
             * keyword comes from config, and injecting the slug here would override it on the
             * user's first filter or sort interaction.
             */
            if (collection?.layout === CollectionLayout.LegacyPromo) {
                const pathSegments = pathname.split('/').filter((segment) => segment !== '');
                const keywordSlug = pathSegments.pop() ?? '';
                newParams = {
                    ...newParams,
                    keyword: keywordSlug,
                };
            } else if (isSEOSearchPage) {
                const parsed = parseSearchSeoPath(pathname);
                if (parsed?.kind === 'plain') {
                    newParams = {
                        ...newParams,
                        keyword: parsed.keyword,
                    };
                }
            } else if (getIsSEOSearchPageWithLocation(pathname)) {
                const parsed = parseSearchSeoPath(pathname);
                if (parsed?.kind === 'location') {
                    newParams = {
                        ...newParams,
                        citySlug: parsed.citySlug,
                        keyword: parsed.keyword,
                        region: parsed.region,
                    };
                }
            }

            // This is to persist the current path when applying filters within the page
            if (isOnCategoryPage || isOnPriceGuidePage) {
                let pathForCategoryNavigate =
                    filterAction === FilterAction.ClearAllFilters ? getPathWithoutParams(pathname, []) : pathname;

                const removedIds = (browseFacetOptionRemovedIds ?? [])
                    .map((id) => String(id))
                    .filter((id) => id !== '');
                if (
                    browseFacetOptionRemovedId !== undefined &&
                    browseFacetOptionRemovedId !== null &&
                    String(browseFacetOptionRemovedId) !== ''
                ) {
                    removedIds.push(String(browseFacetOptionRemovedId));
                }
                if (filterAction !== FilterAction.ClearAllFilters) {
                    for (const removedId of removedIds) {
                        const stripped = removeBrowseFacetOptionFromPath(pathForCategoryNavigate, removedId, rawFacets);
                        if (stripped) {
                            pathForCategoryNavigate = stripped.endsWith('/') ? stripped : `${stripped}/`;
                        }
                    }
                }

                if (filterAction === FilterAction.CategoryChanged || filterAction === FilterAction.CategoryRemoved) {
                    dispatch(clearBrowseSeoMeta());
                }

                const url = getCategoryBrowseNavigateUrl(pathForCategoryNavigate, newParams, rawFacets);
                navigate(url, {
                    state: { currentOpenMenu, filterAction, preventScrollReset, suggestionClicked },
                });
            } else if (collection) {
                const url = getCollectionsUrlWithParams(newParams, collection.slug);
                navigate(url, {
                    state: { currentOpenMenu, filterAction, preventScrollReset, suggestionClicked },
                });
            } else if (isSEOSearchPage || getIsSEOSearchPageWithLocation(pathname)) {
                const clearSubjectPath =
                    Boolean(clearSearchSeoSubjectFromPath) || filterAction === FilterAction.ClearAllFilters;
                const subjectPathSlug = clearSubjectPath
                    ? undefined
                    : (getArtSubjectPathSlugForSeoUrl(newParams, rawFacets) ??
                      parseSearchSeoPath(pathname)?.subjectValueSlug);
                // `artSubject` is stripped from query params unconditionally inside the SEO URL
                // builders (see `getSeoSearchUrlWithParams` / `getSeoSearchUrlWithLocationParams`).
                const url = getIsSEOSearchPageWithLocation(pathname)
                    ? getSeoSearchUrlWithLocationParams(newParams, subjectPathSlug)
                    : getSeoSearchUrlWithParams(newParams, subjectPathSlug);
                navigate(url, {
                    state: { currentOpenMenu, filterAction, preventScrollReset, suggestionClicked },
                });
            } else {
                // The status tabs rebuild a corrected search's params from its keyword alone. While the keyword
                // stays the corrected one, keep the typed word so the "Search instead for" message stays too.
                const { keyword, originalKeyword } = parseQueryParams(searchRef.current);
                if (originalKeyword && !newParams.originalKeyword && String(newParams.keyword) === String(keyword)) {
                    newParams = { ...newParams, originalKeyword };
                }
                const url = getSearchUrlWithParams(newParams);
                navigate(url, {
                    state: { currentOpenMenu, filterAction, preventScrollReset, suggestionClicked },
                });
            }
        },
        [dispatch, isOnCategoryPage, isOnPriceGuidePage, isSEOSearchPage, collection, navigate, pathname, rawFacets]
    );
};
